import React, { useState } from 'react';
import {
  Card, CardHeader, CardTitle, CardBody,
  Spinner,
} from '@patternfly/react-core';
import { SimpleSelect } from '@patternfly/react-templates';
import { ToggleBtn } from '@app/components/ToggleBtn';
import { useTopUsers } from '../hooks/useUsageAnalytics';
import { DAYS_OPTIONS, TopUserPoint } from '../types';

function exportCsv(rows: TopUserPoint[]) {
  const header = 'Name,Email,Calls\n';
  const body = rows.map((r) => `"${r.user_name}","${r.user_email}",${r.count}`).join('\n');
  const blob = new Blob([header + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `top-users-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const TH: React.FC<{
  children: React.ReactNode;
  sortKey: string;
  currentSort: string;
  dir: 'asc' | 'desc';
  onClick: (k: string) => void;
}> = ({ children, sortKey, currentSort, dir, onClick }) => (
  <th className="ua-table__th" onClick={() => onClick(sortKey)}>
    {children}
    {currentSort === sortKey && (
      <span className="ua-table__sort-arrow">{dir === 'asc' ? '▲' : '▼'}</span>
    )}
  </th>
);

interface TopUsersTableProps {
  eventNames: string[];
  cluster?: string | null;
}

export const TopUsersTable: React.FC<TopUsersTableProps> = ({ eventNames, cluster }) => {
  const [days, setDays] = useState<number>(7);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<'user_name' | 'user_email' | 'count'>('count');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, isFetching, error } = useTopUsers(
    days, 20, selectedEvent, cluster,
  );

  const sorted = [...data].sort((a, b) => {
    const av = sortKey === 'count' ? a.count : a[sortKey];
    const bv = sortKey === 'count' ? b.count : b[sortKey];
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function handleSort(key: string) {
    const k = key as typeof sortKey;
    if (sortKey === k) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      setSortDir(k === 'count' ? 'desc' : 'asc');
    }
  }

  const selectOptions = [
    { value: '', content: 'All events' },
    ...eventNames.map((n) => ({ value: n, content: n })),
  ];

  return (
    <Card>
      <CardHeader
        actions={{
          actions: data.length > 0 ? (
            <button type="button" className="ua-csv-btn" onClick={() => exportCsv(data)}>
              ↓ CSV
            </button>
          ) : undefined,
        }}
      >
        <CardTitle>
          Top Users by Activity
          {selectedEvent && (
            <span style={{ fontWeight: 400, fontSize: 14, marginLeft: 10, color: '#6b7280' }}>
              / {selectedEvent}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardBody>
        <div className="ua-controls">
          {/* Event filter */}
          <div className="ua-select-container--sm">
            <SimpleSelect
              initialOptions={selectOptions}
              onSelect={(_e, value) => setSelectedEvent((value as string) || null)}
              placeholder="All events"
            />
          </div>
          {/* Days */}
          <div className="ua-btn-group">
            {DAYS_OPTIONS.map((o) => (
              <ToggleBtn key={o.value} label={o.label} active={days === o.value} onClick={() => setDays(o.value)} />
            ))}
          </div>
          {isFetching && <Spinner size="sm" />}
        </div>

        {error && <div className="ua-error">Error: {error}</div>}

        {isLoading ? (
          <div className="ua-empty-state"><Spinner /></div>
        ) : sorted.length === 0 ? (
          <div className="ua-empty-state">
            No user activity data for the selected period.
          </div>
        ) : (
          <div className="ua-table-wrapper">
            <table className="ua-table">
              <thead>
                <tr>
                  <TH sortKey="user_name" currentSort={sortKey} dir={sortDir} onClick={handleSort}>#</TH>
                  <TH sortKey="user_name" currentSort={sortKey} dir={sortDir} onClick={handleSort}>Name</TH>
                  <TH sortKey="user_email" currentSort={sortKey} dir={sortDir} onClick={handleSort}>Email</TH>
                  <TH sortKey="count" currentSort={sortKey} dir={sortDir} onClick={handleSort}>Calls</TH>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => (
                  <tr key={row.user_email}>
                    <td className="ua-table__rank">{i + 1}</td>
                    <td>{row.user_name}</td>
                    <td className="ua-table__email">{row.user_email}</td>
                    <td className="ua-table__count">{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
